'use client'

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable'
import { motion } from 'framer-motion'
import { useStore } from 'jotai'
import { useTranslations } from 'next-intl'
import {
  isValidElement,
  type MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import { AddIcon, TabCloseIcon } from '@/components/shell-icons'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { OverlayScrollX } from '@/components/widget/overlay-scroll-x'
import {
  type HoverCardSize,
  TabHoverCard,
  TabMarketPreview,
  useTabHoverCard,
  useTabMarketData,
} from '@/components/widget/tab-hover-card'
import { TabSymbolLabel } from '@/components/widget/tab-symbol-label'
import { useMobileLayout } from '@/features/dashboard/lib/mobile-layout-context'
import { useScreenBreakPoint } from '@/hooks/use-screen'
import { getExchangeIconKey } from '@/lib/configs/exchange-registry'
import { toExchangeString } from '@/lib/proto/util'
import { cn } from '@/lib/utils'
import { onActivateKey } from '@/lib/utils/activate-key'
import { getRenderedScale, zoomAwareTranslate } from '@/lib/utils/css-zoom'
import { Exchange } from '@/types/generated/common/types'

const restrictToHorizontalAxis: Modifier = ({ transform }) => ({ ...transform, y: 0 })

// The hover card's md size needs room in both the hovering tab and the widget hosting this
// tab strip — a wide widget with a narrow tab (or vice versa) still reads as cramped, so
// both dimensions must clear their threshold for md.
const HOVER_CARD_MD_MIN_WIDGET_WIDTH_PX = 320
const HOVER_CARD_MD_MIN_TAB_WIDTH_PX = 110

function getTabText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getTabText).join(' ')
  if (isValidElement<{ children?: ReactNode }>(node)) return getTabText(node.props.children)
  return ''
}

/** The real symbol to key market data/live-ticker lookups by — `tab.symbol` when the
 * caller supplied one (i.e. `children` renders a shortened label), else the tab's
 * rendered text, else its key. See `Tab.symbol`'s doc comment. */
function resolveTabSymbol<T>(tab: Tab<T>): string {
  return tab.symbol?.trim() || getTabText(tab.children).trim() || tab.key
}

export type Tab<T = unknown> = {
  key: string
  value: T
  /**
   * The tab's label. Read as plain text and rendered by TabSymbolLabel, which truncates an
   * instrument symbol in the middle so narrow tabs stay distinguishable; only a label with
   * no text content renders as-is.
   */
  children: React.ReactNode
  // Optional stable identifier for animation tracking (e.g., symbol for watchlist tabs)
  // If provided, this is used instead of key to determine if a tab is "new"
  stableId?: string
  exchange?: Exchange
  /**
   * The real underlying instrument symbol (e.g. "ETH-PERPETUAL"), when `children` renders
   * a shortened display label (e.g. "ETH") instead of the raw symbol — as the
   * options-focused tab strips do. Drives both the live-ticker lookup key (so a shortened
   * label doesn't break market-data subscription) and the hover-card's full name. Callers
   * whose `children` already IS the raw symbol can omit this — it falls back to
   * `getTabText(children)`.
   */
  symbol?: string
  /** An option contract: its hover card streams the incremental ticker and drops Funding. */
  isOption?: boolean
}

interface Props<T = unknown> {
  className?: string
  containerClassName?: string
  list: Tab<T>[]
  activeIndex: number
  topLeftCorner?: ReactNode
  topRightCorner?: ReactNode
  onTabChange: (value: T) => void
  onReorder?: (nextList: Tab<T>[]) => void
  removeTab?: (index: number) => void
  renderAddButton?: (defaultButton: ReactNode) => ReactNode
  /** Called when the + button is clicked (opens the instrument picker) */
  onAddRequested: () => void
  /** `data-tutorial` for the + button, for a tutorial step that points at it. */
  addButtonAnchor?: string
  /** Called with the tab index when the already-active tab is clicked, to request editing it */
  onEditRequested?: (index: number) => void
}

interface TabItemProps<T> {
  tab: Tab<T>
  index: number
  isActive: boolean
  isDraggable: boolean
  isReordering: boolean
  shouldAnimate: boolean
  alwaysShowClose: boolean
  className?: string
  onTabChange: (value: T) => void
  onEditRequested?: (index: number) => void
  removeTab?: (index: number) => void
  /** Hover/focus entered this tab — the group relocates the single shared hover-card's
   *  virtual anchor here (after its own open delay, or immediately if one is already showing). */
  onHoverStart: (tab: Tab<T>, index: number, element: HTMLElement) => void
  /** Hover/focus left this tab — the group schedules the shared hover-card closed after its
   *  own close delay, unless the pointer lands on the card's own content first. */
  onHoverEnd: () => void
  /** Pointer-down (click or drag start) — closes the shared hover-card immediately, no delay. */
  onInteractionStart: () => void
  /** Shared-layout id of this strip's active-tab fill, so it slides from tab to tab. */
  activeLayoutId: string
}

// Matches the sortable PointerSensor's activation distance: a press that travelled this far
// was a reorder drag, so its trailing click must not open the picker.
const DRAG_CLICK_TOLERANCE_PX = 4

// Split into its own component because `useSortable` (a hook) needs to be called
// once per tab, which a plain `.map()` callback can't do.
function TabItem<T>({
  tab,
  index,
  isActive,
  isDraggable,
  isReordering,
  shouldAnimate,
  alwaysShowClose,
  className,
  onTabChange,
  onEditRequested,
  removeTab,
  onHoverStart,
  onHoverEnd,
  onInteractionStart,
  activeLayoutId,
}: TabItemProps<T>) {
  const tCommon = useTranslations('common')
  const { attributes, listeners, setNodeRef, node, transform, transition, isDragging } =
    useSortable({
      id: tab.key,
      disabled: !isDraggable,
    })
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null)
  const labelText = getTabText(tab.children).trim()
  // A click on the tab that's already showing opens its picker (it used to take a double
  // click); any other tab just switches.
  const activate = () => {
    if (isActive && onEditRequested) onEditRequested(index)
    else onTabChange(tab.value)
  }

  return (
    <div
      ref={setNodeRef}
      data-tab-key={tab.key}
      style={{
        // Appending `scale(1.02)` while dragging (matching the old whileDrag
        // affordance) directly onto this same transform string, rather than via
        // a separate Tailwind scale utility — Tailwind's scale utilities only
        // apply through its own composed `transform` class, which would silently
        // no-op here since we're setting `transform` via inline style instead.
        transform: [zoomAwareTranslate(transform, node), isDragging ? 'scale(1.02)' : undefined]
          .filter(Boolean)
          .join(' '),
        transition: isDragging ? undefined : transition,
      }}
      // Figma "Widget tab": venue mark, symbol, close; 12px in, 8px out, 8px apart, no
      // dividers. Active = bg/hover with primary text, the rest secondary until hovered.
      className={cn([
        'group/tab relative flex h-9 max-w-[180px] min-w-0 flex-[0_1_180px] cursor-pointer items-center overflow-hidden pr-[calc(8*var(--wpx))] pl-[calc(12*var(--wpx))] whitespace-nowrap transition-colors duration-200',
        isActive
          ? 'text-foreground'
          : 'text-foreground/60 hover:bg-foreground/5 hover:text-foreground/80',
        isDraggable && 'touch-none select-none',
        isDragging && 'z-10 opacity-80 will-change-transform',
        className,
      ])}
      {...(isDraggable ? attributes : {})}
      {...(isDraggable ? listeners : {})}
      onDragStart={isDraggable ? (event) => event.preventDefault() : undefined}
      // Keyboard-reachable whether or not it's sortable (dnd-kit's attributes only
      // add role/tabIndex when dragging is enabled). Drag uses the pointer sensor
      // only, so Enter/Space are free to activate the tab.
      role="button"
      tabIndex={0}
      aria-current={isActive ? 'true' : undefined}
      onKeyDown={onActivateKey(activate)}
      onPointerDownCapture={(event) => {
        pointerDownRef.current = { x: event.clientX, y: event.clientY }
        onInteractionStart()
      }}
      onPointerEnter={(event) => {
        if (isReordering) return
        onHoverStart(tab, index, event.currentTarget)
      }}
      onPointerLeave={() => {
        if (isReordering) return
        onHoverEnd()
      }}
      onFocus={(event) => {
        if (isReordering || !event.target.matches(':focus-visible')) return
        onHoverStart(tab, index, event.currentTarget)
      }}
      onBlur={() => {
        if (isReordering) return
        onHoverEnd()
      }}
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        const down = pointerDownRef.current
        const dragged =
          down !== null &&
          Math.hypot(event.clientX - down.x, event.clientY - down.y) >= DRAG_CLICK_TOLERANCE_PX
        if (dragged) return
        activate()
      }}
    >
      {isActive && (
        // Slides to the newly active tab. layoutDependency pins it to that change only: the
        // tab's own drag transform must not read as a layout move it should chase.
        <motion.span
          layoutId={activeLayoutId}
          layoutDependency={activeLayoutId}
          transition={{ type: 'spring', stiffness: 520, damping: 42 }}
          aria-hidden="true"
          className="bg-foreground/5 absolute inset-0"
        />
      )}
      <div
        className={cn(
          'relative flex w-full min-w-0 items-center gap-[calc(8*var(--wpx))]',
          shouldAnimate && 'animate-[tab-in_320ms_var(--ease-spring)]'
        )}
      >
        {tab.exchange && (
          <ExchangeIcon
            className="size-[calc(12*var(--wpx))] shrink-0 rounded-full"
            exchange={getExchangeIconKey(tab.exchange) ?? toExchangeString(tab.exchange)}
          />
        )}
        {/* The hover-revealed × floats over the label's end (absolute) rather than taking
            room: reserving it with padding widened the tab — and, the strip being w-fit,
            the whole row — on every hover. The label's tail just fades out under it; the
            hover card shows the full symbol anyway. */}
        <div
          className={cn('flex min-w-0 flex-1', removeTab && !alwaysShowClose && 'tab-close-fade')}
        >
          {labelText ? (
            <TabSymbolLabel
              text={labelText}
              className="text-[length:calc(12*var(--wpx))] leading-[calc(16*var(--wpx))] tracking-[-0.02em]"
            />
          ) : (
            tab.children
          )}
        </div>
        {removeTab && (
          <div
            className={cn([
              'flex shrink-0 items-center transition-[opacity,visibility] duration-100',
              alwaysShowClose
                ? 'ml-auto opacity-100'
                : 'invisible absolute top-1/2 right-[calc(4*var(--wpx))] z-10 -translate-y-1/2 opacity-0 delay-0 group-focus-within/tab:visible group-focus-within/tab:opacity-100 group-hover/tab:visible group-hover/tab:opacity-100 group-hover/tab:delay-300',
            ])}
          >
            <button
              aria-label={tCommon('closeTab')}
              type="button"
              className="text-foreground/40 hover:bg-foreground/10 hover:text-foreground flex size-[calc(16*var(--wpx))] shrink-0 cursor-pointer items-center justify-center rounded-xs transition-colors duration-150"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.preventDefault()
                event.stopPropagation()
                removeTab(index)
              }}
            >
              <TabCloseIcon className="size-[calc(8*var(--wpx))]" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export function AddableTabGroup<T = unknown>({
  className,
  containerClassName,
  list,
  activeIndex,
  topLeftCorner,
  topRightCorner,
  onTabChange,
  onReorder,
  removeTab,
  renderAddButton,
  onAddRequested,
  addButtonAnchor,
  onEditRequested,
}: Props<T>) {
  const { isMobile } = useScreenBreakPoint()
  const isMobileStack = useMobileLayout()
  const tabKeys = useMemo(() => list.map((tab) => tab.key), [list])
  const alwaysShowClose = list.length <= 3
  const activeLayoutId = `widget-tab-active-${useId()}`
  const [isReordering, setIsReordering] = useState(false)

  const tabInstruments = useMemo(
    () =>
      list.map((tab) => ({
        exchange: tab.exchange,
        symbol: resolveTabSymbol(tab),
        isOption: tab.isOption,
      })),
    [list]
  )
  useTabMarketData(tabInstruments)

  // Track which tabs have been rendered before (for initial animation only)
  const renderedTabsRef = useRef<Set<string>>(new Set())
  // isNewTab only ever adds to this set — without pruning, a long-lived widget
  // (a chart left open all day, its tabs opened/closed repeatedly) grows it
  // forever for the life of the component. Drop ids no longer present in
  // `list` after each render; if a stableId/key is reused later, it's treated
  // as new again, which is the animation the effect is meant to show anyway.
  useEffect(() => {
    const currentIds = new Set(list.map((tab) => tab.stableId || tab.key))
    for (const id of renderedTabsRef.current) {
      if (!currentIds.has(id)) renderedTabsRef.current.delete(id)
    }
  }, [list])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_CLICK_TOLERANCE_PX } })
  )

  // See useTabHoverCard: one shared card whose anchor moves to the hovered tab.
  const hoverCard = useTabHoverCard({ disabled: isReordering })
  // A tab can disappear through more than the close button (for example, an external
  // watchlist update). Resolve the hovered entry against the current list during render so
  // the popover content is removed in the same commit as its anchor, before Radix can try to
  // reposition against a detached element.
  const visibleHoveredTab = useMemo(
    () => list.find((tab) => tab.key === hoverCard.hoveredKey) ?? null,
    [hoverCard.hoveredKey, list]
  )

  // Widget width (this tab strip's own container, not the viewport), combined with an
  // approximation of each tab's own rendered width, to size the shared hover card (see
  // HOVER_CARD_MD_MIN_*_PX above). Deliberately NOT measured via getBoundingClientRect on
  // the hovered tab element — doing that inside the hover-start handler forces a
  // synchronous layout on every hover (including sweeping across adjacent tabs), which is
  // expensive on a DOM-heavy dashboard page and made the card visibly slower to appear.
  // Every tab shares the same `flex-[0_1_180px]` basis (see TabItem's className below), so
  // `containerWidth / list.length` (capped at that 180px basis) is a cheap, close-enough
  // stand-in for the real per-tab width without touching the DOM at hover time.
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setContainerWidth(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const approxTabWidth = list.length > 0 ? Math.min(180, containerWidth / list.length) : 0
  const hoverCardSize: HoverCardSize =
    containerWidth >= HOVER_CARD_MD_MIN_WIDGET_WIDTH_PX &&
    approxTabWidth >= HOVER_CARD_MD_MIN_TAB_WIDTH_PX
      ? 'md'
      : 'sm'

  const { hoverStart, hoverEnd: handleTabHoverEnd, close: closeHoverCard } = hoverCard
  const scrollerRef = useRef<HTMLDivElement>(null)
  const activeKey = list[activeIndex]?.key
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || activeKey === undefined) return
    const tab = scroller.querySelector<HTMLElement>(
      `[data-tab-key="${window.CSS.escape(activeKey)}"]`
    )
    if (!tab) return
    const scrollerRect = scroller.getBoundingClientRect()
    const tabRect = tab.getBoundingClientRect()
    const scale = getRenderedScale(scroller)
    const tabRight =
      (tabRect.right - scrollerRect.left) / scale + scroller.scrollLeft - scroller.clientLeft
    scroller.scrollTo({ left: Math.max(0, tabRight - scroller.clientWidth), behavior: 'smooth' })
  }, [activeKey])
  const handleTabHoverStart = useCallback(
    (tab: Tab<T>, _index: number, element: HTMLElement) => hoverStart(tab.key, element),
    [hoverStart]
  )

  const handleRemoveTab = useCallback(
    (index: number) => {
      // Also close here for keyboard-initiated button clicks, which do not necessarily emit
      // the pointer-down event handled by the tab container.
      closeHoverCard()
      removeTab?.(index)
    },
    [closeHoverCard, removeTab]
  )

  useEffect(() => {
    if (hoverCard.hoveredKey !== null && !visibleHoveredTab) closeHoverCard()
  }, [hoverCard.hoveredKey, visibleHoveredTab, closeHoverCard])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsReordering(false)
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = list.findIndex((tab) => tab.key === active.id)
      const newIndex = list.findIndex((tab) => tab.key === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      onReorder?.(arrayMove(list, oldIndex, newIndex))
    },
    [list, onReorder]
  )

  // Check if a tab is newly added (should animate)
  // Uses stableId if provided (for tabs with changing keys like watchlist tabs)
  const isNewTab = (tab: Tab<T>) => {
    const trackingId = tab.stableId || tab.key
    if (renderedTabsRef.current.has(trackingId)) {
      return false
    }
    // Mark as rendered after first render
    renderedTabsRef.current.add(trackingId)
    return true
  }

  return (
    <div
      ref={containerRef}
      className={cn([
        'border-foreground/10 flex h-9 max-w-full min-w-0 items-center border-b',
        containerClassName,
      ])}
    >
      {topLeftCorner}
      <div className="flex min-w-0 flex-1 items-center overflow-hidden">
        <OverlayScrollX scrollerRef={scrollerRef} className="flex-[0_1_auto]">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            autoScroll={false}
            onDragStart={() => setIsReordering(true)}
            onDragCancel={() => setIsReordering(false)}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={tabKeys} strategy={horizontalListSortingStrategy}>
              <div className="no-drag flex w-fit max-w-full min-w-0 items-center">
                {list.map((tab, index) => (
                  <TabItem
                    key={tab.stableId || tab.key}
                    tab={tab}
                    index={index}
                    isActive={activeIndex === index}
                    isDraggable={!!onReorder && !isMobile}
                    isReordering={isReordering}
                    shouldAnimate={isNewTab(tab)}
                    alwaysShowClose={alwaysShowClose}
                    className={className}
                    onTabChange={onTabChange}
                    onEditRequested={onEditRequested}
                    removeTab={removeTab ? handleRemoveTab : undefined}
                    onHoverStart={handleTabHoverStart}
                    onHoverEnd={handleTabHoverEnd}
                    onInteractionStart={closeHoverCard}
                    activeLayoutId={activeLayoutId}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <TabHoverCard card={hoverCard}>
            {visibleHoveredTab && (
              <TabMarketPreview
                exchange={visibleHoveredTab.exchange}
                symbol={resolveTabSymbol(visibleHoveredTab)}
                isOption={visibleHoveredTab.isOption}
                cardSize={hoverCardSize}
              />
            )}
          </TabHoverCard>
        </OverlayScrollX>
        {!isMobileStack &&
          (() => {
            const handleAddClick = onAddRequested

            // Figma "Icon button" (Add): 24px ghost square right after the last tab.
            const plusIcon = <AddIcon className="size-[calc(24*var(--wpx))]" />

            const defaultButton = (
              <button
                type="button"
                data-tutorial={addButtonAnchor}
                className="text-foreground/60 hover:bg-foreground/5 hover:text-foreground group flex size-[calc(24*var(--wpx))] shrink-0 cursor-pointer items-center justify-center rounded-sm transition-colors duration-150"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation()
                  if (!renderAddButton) handleAddClick()
                }}
              >
                {plusIcon}
              </button>
            )

            return renderAddButton ? renderAddButton(defaultButton) : defaultButton
          })()}
        {!isMobileStack && <span className="h-full w-3" />}
        {!isMobileStack && topRightCorner}
      </div>
    </div>
  )
}
